import { DisabledPrompt, useStateManager, BrandingStyle } from "@bcm-dev/bcms-vite";
import { FormRowView, TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableView } from "components/shared";
import { UITextInput } from "components/ui";
import React from "react";
import "./styles.css";
import "./mobile.css";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { SVGAsset } from "res";
import CustomAccordion, { CustomAccordionContent, CustomAccordionToggle } from "components/custom-ui/CustomAccordion";
import { FormContainer } from 'components/shared';
import IconToggle from "components/shared/views/IconToggle";

/**
 * @function TASPositivePayCreateTemplateItemTypeMappingWidget
 * @return {React.Component}
 * @exported true
 * @description The Import File Mapping Preview table for the ImportFileMappingPreviewWidget.
 */
export default function TASPositivePayCreateTemplateItemTypeMappingWidget(props) {
    const { data, onChangeCheckTemp } = useStateManager();
    const columns = [{ id: "0", value: "Item Type Mapping" }, { id: "1", value: "Value" }, { id: "2", value: "Delete" }];
    const checkTemplate = data.checkTemplate;
    const columnsItems = columns.map(item => {
        return <TableColumnView key={item.id} text={ item.value } />
    }, []);

    return (
        <div className="tas-positive-pay-create-template-item-type-mapping-widget">
            <DesktopWidget columns={ columns } checkTemplate={ checkTemplate } onChangeCheckTemp={ onChangeCheckTemp } />
            <MobileWidget columns={ columns } checkTemplate={ checkTemplate } onChangeCheckTemp={ onChangeCheckTemp }/>
        </div>
    );
}

function DesktopWidget(props) {
    const columns = props.columns;
    const checkTemplate = props.checkTemplate;
    const onChangeCheckTemp = props.onChangeCheckTemp;
    const columnsItems = columns.map(item => {
        return <TableColumnView key={item.id} text={ item.value } />
    }, []);

    const items = checkTemplate.customItemTypeCodes.reduce((acc, item) => {
        acc.push(
            <Row key={ item.id } item={ item } checkTemplate={ checkTemplate } />
        );
        return acc;
    }, []);

    return (
        <>
            <div className="flex-container flex-horizontal align-center widget-title">
                <h2 className="label-with-icon">
                    <span data-point="TAS_Positive_Pay_Create_Template_Item_Type_Mapping_Widget_Title">Custom Item Type Code</span>
                </h2>
                <SVGAsset id="InfoIcon" />
            </div>
            <DisabledPrompt>
                <div className="flex-container add-link-container">
                    <a className="clickable add-link set-width">+ Add</a>
                </div>
            </DisabledPrompt>
            <TableView className="tas-positive-pay-create-template-item-type-mapping-table show-table-on-desktop">
                <TableColumnContainer>
                    { columnsItems }
                </TableColumnContainer>
                <TableBody>
                    { items }
                </TableBody>
            </TableView>
        </>
    );
}

function Row(props) {
    const { ui, onChangeItemType  } = useStateManager();
    const item = props.item ?? {};
    const customItemTypeCodesUI = ui.checkTemplate.customItemTypeCodes;
    const customItemTypeCodeUI = customItemTypeCodesUI.filter(_item => _item.id === item.id)[0]

    const types = [
        {id: "0", value: "issue", label: "Issue" },
        {id: "1", value: "stop", label: "Stop" },
        {id: "2", value: "void", label: "Void" }
    ]

    const items = types.reduce((acc, item) => {
        acc.push(
            <CustomSelectItem key={ item.id } value={ item.value }>{ `${ item.label }` }</CustomSelectItem>
        );
        return acc;
    }, []);

    const select = (
        <CustomSelect dataPoint={ `TAS_Positive_Pay_Create_Template_Item_Type_Mapping_Widget_Select_${ item.id }` } 
            value={ item.itemTypeMapping } onChange={ onChangeItemType(item.id, "itemTypeMapping") }
            active={ customItemTypeCodeUI.itemTypeMapping.isActive }>
            { items }
        </CustomSelect>
    );
    
    return (
        <TableItemContainer>
            <TableItemTextView className="column-item column-one" 
            text={ select } />
            <TableItemTextView className="column-item column-two" 
            text={ <UITextInput value={ item.itemTypeMappingValue }
            dataPoint={ `TAS_Positive_Pay_Create_Template_Item_Type_Mapping_Widget_Value_Input_${ item.id }` }
            onChange={ onChangeItemType(item.id, "itemTypeMappingValue") } /> } />
            <TableItemTextView className="column-item column-three" 
            text={ <DisabledPrompt><SVGAsset className="clickable" id="TrashIcon"
            data-point={ `TAS_Positive_Pay_Create_Template_Item_Type_Mapping_Widget_Delete_Icon_${ item.id }` } /></DisabledPrompt> } />
        </TableItemContainer>
    );
}

function MobileWidget(props) {
    const { data, ui } = useStateManager();
    const customItemTypeCodes = data.checkTemplate.customItemTypeCodes;

    const rows = customItemTypeCodes.reduce((acc, item, index) => {
        const amount = item.amount ? item.amount.amount : undefined;
        const isEditing = true;
        
        acc.push(
            isEditing
            ? <EditingRow key={ item.id } amount={ amount  } item={ item } editItem={ item }/>
            : <Row key={ item.id } amount={ amount  } item={ item }/>
        );
        return acc;
    }, []);
    
    return (
        <div className="tas-positive-pay-exception-checks-mobile-content-view-container">
            { rows }
        </div>
    );
}

/**
 * @function EditingRow
 * @return {React.Component}
 * @exported false
 * @description A row that can be edited by the user.
 */
function EditingRow(props) {
  const { data, ui, onEdit, onChangeItemType, onMobileCloseButtonClick } = useStateManager();
  const item = props.editItem ?? {};
  const uiItem = ui.checkTemplate.customItemTypeCodes.filter(_item => _item.customItemTypeCode.id === item.id)[0];
  
  const types = [
        {id: "0", value: "issue", label: "Issue" },
        {id: "1", value: "stop", label: "Stop" },
        {id: "2", value: "void", label: "Void" }
    ]

    const typeItems = types.reduce((acc, item) => {
        acc.push(
            <CustomSelectItem key={ item.id } value={ item.value }>{ `${ item.label }` }</CustomSelectItem>
        );
        return acc;
    }, []);

  const chevronIconClosedClass = uiItem.mobile.isActive ? "" : "closed";

  return (
    <div key={ item.id } className="show-on-mobile">
        <div className="flex-container align-top">
            <CustomAccordion isOpen={ uiItem.mobile.isActive }>
                <div className="flex-container flex-horizontal align-top check-custom-accordion-content">
                        <div className="set-width">
                            <CustomAccordionToggle className={ "" } onClick={ onMobileCloseButtonClick(item.id) }>
                                <IconToggle className={ `chevron-icon-container ${ chevronIconClosedClass }`} icon="DownChevronIcon" text=""/>
                            </CustomAccordionToggle>
                        </div>
                        <div className="flex-width">
                            <div className="flex-container flex-vertical check-details">
                                <div className="check-detail-row item-type-mapping-row">
                                    <span className="sub-text">Item Type Mapping: </span>
                                    <b>{ item.itemTypeMapping }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Value: </span>
                                    <b>{ item.itemTypeMappingValue }</b>
                                </div>
                            </div>
                        </div>
                </div>
                <CustomAccordionContent className="flex-container flex-vertical tas-positive-pay-add-check-page-accordion-content">
                    <CustomAccordionToggle className="flex-container flex-width close-accordion accordion-edit-view" onClick={ onMobileCloseButtonClick(item.id) }>
                        <BrandingStyle className="flex-width sub-text" element={<p >DETAILS</p>}/>
                        <SVGAsset className="icon set-width" id="CloseX" data-point="CloseButton" />
                    </CustomAccordionToggle>
                    <FormContainer className="small-form-width">
                        <FormRowView className="flex-width action-row-view" text="Item Type Mapping">
                            <CustomSelect active={  uiItem.itemTypeMapping.isActive } value={ item.itemTypeMapping }
                                dataPoint={ `TAS_Positive_Pay_View_Checks_Desktop_Edit_Row_Type_${ item.id }`}
                                onChange={ onChangeItemType(item.id, "itemTypeMapping") }>
                                { typeItems }
                            </CustomSelect>
                        </FormRowView>
                        <FormRowView className="sub-text" text="">
                            <UITextInput value={ item.itemTypeMappingValue } onChange={ onChangeItemType(item.id, "itemTypeMappingValue") } />
                        </FormRowView>
                        <FormRowView className="sub-text" text="">
                            <DisabledPrompt>
                                <span>
                                <a className="delete-link">
                                    <span className="clickable">{ `Delete` }</span>
                                </a>
                                </span>
                                
                            </DisabledPrompt>
                        </FormRowView>
                    </FormContainer>
                </CustomAccordionContent>
            </CustomAccordion>
        </div>
    </div>
  );
}